import { Icon, VFlow } from 'bold-ui'
import { usePecField } from 'components/form'
import { LoadingIndicator } from 'components/loading'
import { NavigationButton } from 'components/route/NavigationButton'
import { css } from 'emotion'
import { useUltimoMChatQuery } from 'graphql/hooks.generated'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { isEmpty } from 'lodash'
import React from 'react'
import { useRouteMatch } from 'react-router'
import { calculateAge } from 'util/date/calculateAge'
import { MetaPath } from 'util/metaPath'

import { ACOMPANHAMENTO_CRIANCA_URL_MODAL } from '../../../aside/acompanhamento-puericultura/model'
import { MChatRfAccordion } from './components/MChatRfAccordion'
import { ConsultaSeguimentoField } from './consulta-seguimento/consulta-seguimento-field/ConsultaSeguimentoField'
import { convertMChatRfQueryToViewModel } from './converter-mChatRf'
import { MChatField } from './mchat/mchat-field/MChatField'
import { getMChatAplicacaoPerguntasFalhas } from './mchat/utils-mchat'
import { MChatRfFieldModel } from './model-mChatRf'
import { isAccordionPreExpanded } from './utils-mChatRf'

interface MChatRfFieldProps {
  name: MetaPath<MChatRfFieldModel>
}

export function MChatRfField(props: Readonly<MChatRfFieldProps>) {
  const { name } = props

  const match = useRouteMatch()
  const modalAcompanhamentoCriancaUrl = `${match.url}${ACOMPANHAMENTO_CRIANCA_URL_MODAL}`

  const {
    input: { value },
  } = usePecField({ name: name, subscription: { value: true } })

  const mChatAtual = value?.mchat
  const consultaSeguimentoAtual = value?.consultaSeguimento

  const {
    atendimentoProfissional: { iniciadoEm: dataAtendimento },
    cidadao: { dataNascimento },
    prontuario: { id: prontuarioId },
  } = useAtendimentoContext()

  const {
    data: { ultimoMChatR, ultimoMChatRf },
    loading,
  } = useUltimoMChatQuery({
    variables: {
      prontuarioId,
    },
  })

  const hasValuesAntendimentoAnterior = !loading && (!isEmpty(ultimoMChatR) || !isEmpty(ultimoMChatRf))

  const hasMChatAterior = !isEmpty(ultimoMChatR)
  const hasConsultaSeguimentoAnterior = !isEmpty(ultimoMChatRf)
  const hasConsultaSeguimentoAtual = !isEmpty(consultaSeguimentoAtual)
  const hasMChatAnteriorOrConsultaSeguimento = hasMChatAterior || hasConsultaSeguimentoAnterior

  const hasConsultaSeguimento = hasConsultaSeguimentoAtual || hasConsultaSeguimentoAnterior

  const valuesAtendimentoAnterior = convertMChatRfQueryToViewModel(ultimoMChatR, ultimoMChatRf)

  const { ageInMonths } = calculateAge(dataNascimento, dataAtendimento.getTime())
  const { ageInMonths: ageInMonthsAtendimentoAnterior } =
    hasMChatAterior && calculateAge(dataNascimento, ultimoMChatR.dataResultado)

  const isMChatAnteriorRiscoBaixo = valuesAtendimentoAnterior?.mchat?.risco === MChatRiscoEnum.BAIXO

  const mChatAtualPerguntasFalhas = getMChatAplicacaoPerguntasFalhas(mChatAtual?.aplicacao)
  const mChatAnteriorPerguntasFalhas = getMChatAplicacaoPerguntasFalhas(valuesAtendimentoAnterior?.mchat?.aplicacao)

  const isPreExpanded = isAccordionPreExpanded({
    ageInMonths,
    ageInMonthsAtendimentoAnterior,
    hasValuesAntendimentoAnterior,
    isMChatAnteriorRiscoBaixo,
  })

  if (loading) {
    return <LoadingIndicator message='Carregando dados do M-CHAT-R/F™...' />
  }

  return (
    <VFlow>
      <MChatRfAccordion isPreExpanded={isPreExpanded} isLoading={loading}>
        <VFlow vSpacing={0}>
          <MChatField
            name={name.mchat}
            valuesAtendimentoAnterior={valuesAtendimentoAnterior?.mchat}
            hasConsultaSeguimento={hasConsultaSeguimento}
          />
          <ConsultaSeguimentoField
            name={name.consultaSeguimento}
            valuesAtendimentoAnterior={valuesAtendimentoAnterior?.consultaSeguimento}
            mChatAtualRisco={mChatAtual?.risco}
            mChatAnteriorRisco={valuesAtendimentoAnterior?.mchat?.risco}
            mChatAtualPerguntasFalhas={mChatAtualPerguntasFalhas}
            mChatAnteriorPerguntasFalhas={mChatAnteriorPerguntasFalhas}
          />
        </VFlow>
        {hasMChatAnteriorOrConsultaSeguimento && (
          <NavigationButton
            kind='primary'
            size='small'
            skin='outline'
            style={styles.section}
            to={{
              pathname: modalAcompanhamentoCriancaUrl,
              state: { focusTitle: true },
            }}
          >
            <Icon icon='zoomOutline' style={styles.icon} />
            Histórico de resultados
          </NavigationButton>
        )}
      </MChatRfAccordion>
    </VFlow>
  )
}

const styles = {
  section: css`
    margin: 1rem;
  `,
  icon: css`
    margin-right: 0.5rem;
  `,
}
